import {
  getAxiosMergedUserUpdateRequests,
  getAxiosUserUpdateRequests,
  putAxiosApproveUserUpdateRequest,
  putAxiosDeclineUserUpdateRequest,
} from "@API/UserUpdateRequests/endpoints";
import { IGetAxiosUserUpdateRequests } from "@API/UserUpdateRequests/types";
import { getOrganizationByIdSuccess } from "@Store/Organizations/actions";
import { IOrganization } from "@Store/Organizations/types";
import { usersListRequest } from "@Store/Users/actions";
import i18n from "@i18n";
import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, isAxiosError } from "axios";
import { call, put, takeLatest } from "redux-saga/effects";
import {
  approveUserUpdateRequest,
  approveUserUpdateRequestFail,
  approveUserUpdateRequestSuccess,
  declineUserUpdateRequest,
  declineUserUpdateRequestFail,
  declineUserUpdateRequestSuccess,
  setHasPendingRequest,
  userUpdateRequestsListFail,
  userUpdateRequestsListRequest,
  userUpdateRequestsListSuccess,
} from "./actions";
import { IUserUpdateRequestActionPayload } from "./types";
import { OrganizationId } from "@Store/Organizations/types";
import { showSnackbar } from "@Store/toast/actions";

function getErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return axiosError.response?.data?.message ?? error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return i18n.t("pendingUserRequests.messages.genericError");
}

function* fetchHasPendingRequest(orgId: OrganizationId) {
  const response: IGetAxiosUserUpdateRequests = yield call(
    getAxiosUserUpdateRequests,
    orgId,
    { pending: true, limit: 1 }
  );
  if (response.error) throw response.error;

  yield put(setHasPendingRequest(response.data.length > 0));
}

function* onGetOrganizationByIdSuccess({
  payload,
}: PayloadAction<IOrganization>) {
  try {
    yield call(fetchHasPendingRequest, payload.orgId);
  } catch (e) {
    console.error(e);
    yield put(setHasPendingRequest(false));
  }
}

function* onUserUpdateRequestsListRequest({
  payload,
}: PayloadAction<OrganizationId>) {
  try {
    const response: IGetAxiosUserUpdateRequests = yield call(
      getAxiosMergedUserUpdateRequests,
      payload
    );
    if (response.error) throw response.error;

    yield put(userUpdateRequestsListSuccess(response.data));
  } catch (e) {
    console.error(e);
    yield put(userUpdateRequestsListFail(e as Error));
    yield put(showSnackbar(getErrorMessage(e), "error", e));
  }
}

function* onApproveUserUpdateRequest({
  payload,
}: PayloadAction<IUserUpdateRequestActionPayload>) {
  try {
    yield call(
      putAxiosApproveUserUpdateRequest,
      payload.orgId,
      payload.requestId
    );

    yield put(approveUserUpdateRequestSuccess(payload));
    yield put(
      showSnackbar(
        i18n.t("pendingUserRequests.messages.approveSuccess"),
        "success"
      )
    );
    yield put(userUpdateRequestsListRequest(payload.orgId));
    yield put(usersListRequest({ orgId: payload.orgId }));
  } catch (e) {
    console.error(e);
    yield put(approveUserUpdateRequestFail(e as Error));
    const message = getErrorMessage(e);
    yield put(
      showSnackbar(
        isAxiosError(e) && e.response?.status === 400
          ? i18n.t("pendingUserRequests.messages.alreadyProcessed")
          : message,
        "error",
        e
      )
    );
  }
}

function* onDeclineUserUpdateRequest({
  payload,
}: PayloadAction<IUserUpdateRequestActionPayload>) {
  try {
    yield call(
      putAxiosDeclineUserUpdateRequest,
      payload.orgId,
      payload.requestId
    );

    yield put(declineUserUpdateRequestSuccess(payload));
    yield put(
      showSnackbar(
        i18n.t("pendingUserRequests.messages.declineSuccess"),
        "success"
      )
    );
    yield put(userUpdateRequestsListRequest(payload.orgId));
  } catch (e) {
    console.error(e);
    yield put(declineUserUpdateRequestFail(e as Error));
    const message = getErrorMessage(e);
    yield put(
      showSnackbar(
        isAxiosError(e) && e.response?.status === 400
          ? i18n.t("pendingUserRequests.messages.alreadyProcessed")
          : message,
        "error",
        e
      )
    );
  }
}

export default function* UserUpdateRequestsSaga() {
  yield takeLatest(getOrganizationByIdSuccess.type, onGetOrganizationByIdSuccess);
  yield takeLatest(
    userUpdateRequestsListRequest,
    onUserUpdateRequestsListRequest
  );
  yield takeLatest(approveUserUpdateRequest, onApproveUserUpdateRequest);
  yield takeLatest(declineUserUpdateRequest, onDeclineUserUpdateRequest);
}
